import type { CvtConcernDoc } from '@web/vin-sessions/types';
import type { TextStyle, ViewStyle } from 'react-native';

import { colors } from '@packages/core/styles';
import { Button, Icon, LoadingCircle, Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { CvtConcernDocListItem } from './cvt-concern-doc-list-item';

const linkButtonStyle: ViewStyle = { flexShrink: 1, height: 'auto' };
const typographyStyle: TextStyle = { lineHeight: 24 };

const useStyles = createUseStyles({
    placeholder: {
        alignItems: 'center',
        backgroundColor: colors.grayOne,
        borderRadius: 4,
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        padding: 16,
    },
});

export type CvtConcernDocListProps = {
    /** The CVT concern documents to list. */
    cvtConcernDocs: CvtConcernDoc[];

    /** Whether the CVT concern documents failed to load. */
    hasFailedToLoadCvtConcernDocs: boolean;

    /** Whether the CVT concern documents are still loading. */
    isLoadingCvtConcernDocs: boolean;

    /** Opens the CVT concern document at the given URL in a new tab. */
    onDocumentOpen: (preSignedUrl: string) => void;
};

/**
 * The list of CVT concern documents in the CVT primary concerns modal.
 *
 * Renders a grey box with a spinner while the documents load, the same box with a message if they failed to load or
 * there are none, or a link per document, labelled as a TSB.
 */
export function CvtConcernDocList({
    cvtConcernDocs,
    hasFailedToLoadCvtConcernDocs,
    isLoadingCvtConcernDocs,
    onDocumentOpen,
}: CvtConcernDocListProps) {
    const { t } = useTranslation();
    const classes = useStyles();

    const hasCvtConcernDocs = cvtConcernDocs.length > 0;

    if (isLoadingCvtConcernDocs) {
        return (
            <div className={classes.placeholder}>
                <LoadingCircle />
            </div>
        );
    }

    if (hasFailedToLoadCvtConcernDocs) {
        return (
            <div className={classes.placeholder}>
                <Icon name="info" />
                <Typography style={typographyStyle}>
                    {t(
                        'cvt:notRequiredModal.documents.tsbLoadError',
                        'We couldn’t load the related TSBs. Please close this window and try again.'
                    )}
                </Typography>
            </div>
        );
    }

    if (!hasCvtConcernDocs) {
        return (
            <div className={classes.placeholder}>
                <Icon name="info" />
                <Typography style={typographyStyle}>
                    {t('cvt:notRequiredModal.documents.noTsbs', 'No related TSBs were found.')}
                </Typography>
            </div>
        );
    }

    return (
        <ul className="d-flex flex-column gap-2 mb-0">
            {cvtConcernDocs.map(({ documentCode, preSignedUrl, title }) => (
                <CvtConcernDocListItem key={documentCode}>
                    <Button
                        alignTitle="left"
                        onPress={() => onDocumentOpen(preSignedUrl)}
                        size="small"
                        style={linkButtonStyle}
                        variant="link-blue"
                    >
                        {`TSB ${documentCode}: ${title}`}
                    </Button>
                </CvtConcernDocListItem>
            ))}
        </ul>
    );
}
